import React from 'react';
import { Link } from 'react-router-dom';
import { User, HandHeart, Stethoscope, ExternalLink, Phone, ArrowRight, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { resources, faqItems } from '../data/mock';

const ResourcesPage = () => {
  const iconMap = {
    User: User,
    HandHeart: HandHeart,
    Stethoscope: Stethoscope
  };

  const externalResources = [
    {
      name: 'American Cancer Society',
      description: 'National resources for cancer patients and families',
      url: 'https://www.cancer.org'
    },
    {
      name: 'National Cancer Institute',
      description: 'Comprehensive cancer information and research',
      url: 'https://www.cancer.gov'
    },
    {
      name: 'CancerCare',
      description: 'Free professional support services',
      url: 'https://www.cancercare.org'
    },
    {
      name: 'Hawaii State Department of Health',
      description: 'Local health resources and programs',
      url: 'https://health.hawaii.gov'
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="bg-[#5D4E6D] py-24 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#E07A5F]/20 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#E07A5F] font-semibold text-sm uppercase tracking-wider">Resources</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6 leading-tight">
              Support & Resources
            </h1>
            <p className="text-gray-200 text-lg leading-relaxed">
              Access comprehensive resources for cancer patients, caregivers, and families. 
              We're here to help you navigate your journey with the support you need.
            </p>
          </div>
        </div>
      </section>

      {/* Resource Categories */}
      <section className="py-24 bg-[#F5F0EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {resources.map((resource) => {
              const IconComponent = iconMap[resource.icon] || User;
              return (
                <Card
                  key={resource.id}
                  className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 group"
                >
                  <div className="h-2 bg-gradient-to-r from-[#5D4E6D] to-[#E07A5F]" />
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-[#E07A5F]/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-[#E07A5F] transition-colors duration-300">
                      <IconComponent className="w-8 h-8 text-[#E07A5F] group-hover:text-white transition-colors duration-300" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#2D2A32] mb-3">{resource.title}</h3>
                    <p className="text-gray-600 mb-6">{resource.description}</p>
                    <ul className="space-y-3">
                      {resource.items.map((item) => (
                        <li key={item} className="flex items-center text-gray-700">
                          <span className="w-2 h-2 bg-[#5D4E6D] rounded-full mr-3" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* External Resources */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#E07A5F] font-semibold text-sm uppercase tracking-wider">Helpful Links</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2D2A32] mt-4">
              External Resources
            </h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              Additional trusted resources for cancer information and support.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {externalResources.map((resource) => (
              <a
                key={resource.url}
                href={resource.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block p-6 bg-[#F5F0EB] rounded-2xl hover:shadow-lg transition-all duration-300 group"
              >
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-lg font-bold text-[#2D2A32] group-hover:text-[#5D4E6D] transition-colors">
                    {resource.name}
                  </h3>
                  <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-[#E07A5F] transition-colors" />
                </div>
                <p className="text-gray-600 text-sm">{resource.description}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-[#F5F0EB]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#E07A5F] font-semibold text-sm uppercase tracking-wider">FAQ</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2D2A32] mt-4">
              Frequently Asked Questions
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqItems.map((item, index) => (
              <AccordionItem
                key={item.question}
                value={`item-${index}`}
                className="bg-white rounded-2xl shadow-md border-0 px-6 overflow-hidden"
              >
                <AccordionTrigger className="text-left font-semibold text-[#2D2A32] hover:text-[#5D4E6D] py-6 hover:no-underline">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-6">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Emergency Support */}
      <section className="py-16 bg-[#E07A5F]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Need Immediate Support?
          </h2>
          <p className="text-white/90 mb-6">
            If you or a loved one needs immediate support, please don't hesitate to reach out.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-white text-[#E07A5F] hover:bg-gray-100 rounded-full px-8 transition-all duration-300"
            >
              <a href="tel:(808)555-0123">
                <Phone className="mr-2 w-5 h-5" />
                Call Us Now
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-[#E07A5F] rounded-full px-8 transition-all duration-300 bg-transparent"
            >
              <Link to="/contact">
                Contact Form
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ResourcesPage;
